import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import { useNavbarUser } from '../../utils/navbarUser';
import supportApi from '../../services/supportApi';
import './SupportPage.css';

const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting on Support',
  resolved: 'Resolved',
  closed: 'Closed',
};

const DEFAULT_FORM = {
  subject: '',
  category: '',
  message: '',
  priority: 'medium',
};

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function SupportPage() {
  const user = useNavbarUser();

  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [lastTicketNumber, setLastTicketNumber] = useState('');

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState('');
  const [openTicketId, setOpenTicketId] = useState('');
  const [openTicketDetail, setOpenTicketDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState('');

  useEffect(() => {
    supportApi.getSupportMetadata()
      .then((data) => setCategories(Array.isArray(data?.categories) ? data.categories : []))
      .catch(() => setCategories([]));
  }, []);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    setTicketsError('');
    try {
      const data = await supportApi.getMyTickets();
      setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch (error) {
      setTicketsError(error.message || 'Failed to load your tickets.');
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrioritySelect = (priority) => {
    setFormData((prev) => ({ ...prev, priority }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setAttachmentFile(file);
  };

  const isFormValid = formData.subject.trim() && formData.category && formData.message.trim();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');
    setLastTicketNumber('');

    try {
      let attachmentUrl = null;
      if (attachmentFile) {
        const uploadResult = await supportApi.uploadSupportAttachment(attachmentFile);
        attachmentUrl = uploadResult?.attachmentUrl || null;
      }

      const result = await supportApi.submitTicket({
        subject: formData.subject.trim(),
        category: formData.category,
        message: formData.message.trim(),
        priority: formData.priority,
        attachmentUrl,
      });

      setLastTicketNumber(result?.ticketNumber || '');
      setFormData(DEFAULT_FORM);
      setAttachmentFile(null);
      const fileInput = document.getElementById('support-attachment');
      if (fileInput) fileInput.value = '';
      await loadTickets();
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTicket = useCallback(async (ticketId) => {
    if (openTicketId === ticketId) {
      setOpenTicketId('');
      setOpenTicketDetail(null);
      setReplyMessage('');
      setReplyError('');
      return;
    }

    setOpenTicketId(ticketId);
    setOpenTicketDetail(null);
    setReplyMessage('');
    setReplyError('');
    setDetailLoading(true);

    try {
      const data = await supportApi.getTicketDetail(ticketId);
      setOpenTicketDetail(data);
    } catch (error) {
      setReplyError(error.message || 'Failed to load ticket.');
    } finally {
      setDetailLoading(false);
    }
  }, [openTicketId]);

  const handleReply = async (event) => {
    event.preventDefault();
    if (!replyMessage.trim() || replySubmitting || !openTicketId) return;

    setReplySubmitting(true);
    setReplyError('');

    try {
      await supportApi.replyToTicket(openTicketId, replyMessage.trim());
      setReplyMessage('');
      const data = await supportApi.getTicketDetail(openTicketId);
      setOpenTicketDetail(data);
      await loadTickets();
    } catch (error) {
      setReplyError(error.message || 'Failed to send reply.');
    } finally {
      setReplySubmitting(false);
    }
  };

  const hasTickets = tickets.length > 0;

  const categoryOptions = useMemo(() => (
    categories.length > 0
      ? categories
      : [
          { id: 'account_issues', label: 'Account Issues' },
          { id: 'payment_billing', label: 'Payment & Billing' },
          { id: 'streaming_problems', label: 'Streaming Problems' },
          { id: 'obs_overlay_issues', label: 'OBS & Overlay Issues' },
          { id: 'content_moderation', label: 'Content Moderation Appeal' },
          { id: 'spades_currency', label: 'Spades & Virtual Currency' },
          { id: 'technical_support', label: 'Technical Support' },
          { id: 'report_user', label: 'Report a User' },
          { id: 'other', label: 'Other' },
        ]
  ), [categories]);

  return (
    <div className="support-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={() => {}}
      />

      <LeftSidebar />

      <main className="support-page__main">
        <div className="support-page__content">
          <h1 className="support-page__title">Support</h1>

          <div className="support-page__card">
            <p className="support-page__intro">
              Have a question or need help? Submit a ticket below and our support team will reply by email and in the conversation below.
            </p>

            {lastTicketNumber && (
              <div className="support-page__msg support-page__msg--success">
                <strong>Ticket {lastTicketNumber} submitted.</strong> We&apos;ve emailed you a confirmation. Replies from our team will appear in the thread below.
              </div>
            )}
            {submitError && (
              <div className="support-page__msg support-page__msg--error">{submitError}</div>
            )}

            <form className="support-page__form" onSubmit={handleSubmit}>
              <div className="support-page__field">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Brief description of your issue"
                  maxLength={200}
                />
              </div>

              <div className="support-page__field">
                <label htmlFor="category">Category</label>
                <select id="category" name="category" value={formData.category} onChange={handleChange}>
                  <option value="">Select a category</option>
                  {categoryOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="support-page__field">
                <label>Priority</label>
                <div className="support-page__priority-row">
                  {['low', 'medium', 'high', 'urgent'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`support-page__priority-btn support-page__priority-btn--${p}${formData.priority === p ? ' support-page__priority-btn--active' : ''}`}
                      onClick={() => handlePrioritySelect(p)}
                    >
                      {PRIORITY_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="support-page__field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please describe your issue in detail..."
                  rows={6}
                />
              </div>

              <div className="support-page__field">
                <label htmlFor="support-attachment">Attachment (optional)</label>
                <input
                  id="support-attachment"
                  type="file"
                  accept="image/*,application/pdf,video/mp4"
                  onChange={handleFileChange}
                />
                {attachmentFile && (
                  <span className="support-page__file-hint">{attachmentFile.name}</span>
                )}
              </div>

              <button
                type="submit"
                className="support-page__submit-btn"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Submit Ticket'}
              </button>
            </form>
          </div>

          <section className="support-page__tickets">
            <h2 className="support-page__tickets-title">My Tickets</h2>
            {ticketsLoading ? (
              <div className="support-page__empty">Loading your tickets...</div>
            ) : ticketsError ? (
              <div className="support-page__msg support-page__msg--error">{ticketsError}</div>
            ) : !hasTickets ? (
              <div className="support-page__empty">You haven&apos;t submitted any tickets yet.</div>
            ) : (
              <div className="support-page__ticket-list">
                {tickets.map((ticket) => {
                  const isOpen = openTicketId === ticket.id;
                  return (
                    <div
                      key={ticket.id}
                      className={`support-page__ticket${isOpen ? ' support-page__ticket--open' : ''}`}
                    >
                      <button
                        type="button"
                        className="support-page__ticket-summary"
                        onClick={() => openTicket(ticket.id)}
                      >
                        <div className="support-page__ticket-summary-left">
                          <div className="support-page__ticket-number">{ticket.ticketNumber}</div>
                          <div className="support-page__ticket-subject">{ticket.subject}</div>
                        </div>
                        <div className="support-page__ticket-summary-right">
                          <span className={`support-page__badge support-page__badge--priority-${ticket.priority}`}>
                            {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                          </span>
                          <span className={`support-page__badge support-page__badge--status-${ticket.status}`}>
                            {STATUS_LABELS[ticket.status] || ticket.status}
                          </span>
                        </div>
                      </button>

                      {isOpen && (
                        <div className="support-page__ticket-body">
                          {detailLoading ? (
                            <div className="support-page__empty">Loading conversation...</div>
                          ) : openTicketDetail ? (
                            <>
                              <div className="support-page__thread-meta">
                                <span>Category: {openTicketDetail.ticket.categoryLabel}</span>
                                <span>Opened: {formatDate(openTicketDetail.ticket.createdAt)}</span>
                              </div>

                              <div className="support-page__msg-bubble support-page__msg-bubble--user">
                                <div className="support-page__msg-bubble-header">You — {formatDate(openTicketDetail.ticket.createdAt)}</div>
                                <div className="support-page__msg-bubble-body">{openTicketDetail.ticket.message}</div>
                                {openTicketDetail.ticket.attachmentUrl && (
                                  <a
                                    href={openTicketDetail.ticket.attachmentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="support-page__attachment-link"
                                  >
                                    View attachment
                                  </a>
                                )}
                              </div>

                              {(openTicketDetail.replies || []).map((reply) => (
                                <div
                                  key={reply.id}
                                  className={`support-page__msg-bubble support-page__msg-bubble--${reply.authorType === 'support' ? 'support' : 'user'}`}
                                >
                                  <div className="support-page__msg-bubble-header">
                                    {reply.authorType === 'support' ? 'Support team' : 'You'} — {formatDate(reply.createdAt)}
                                  </div>
                                  <div className="support-page__msg-bubble-body">{reply.message}</div>
                                </div>
                              ))}

                              {['resolved', 'closed'].includes(openTicketDetail.ticket.status) ? (
                                <div className="support-page__empty">This ticket is {STATUS_LABELS[openTicketDetail.ticket.status]?.toLowerCase()}. Submit a new ticket if you need further help.</div>
                              ) : (
                                <form className="support-page__reply-form" onSubmit={handleReply}>
                                  <textarea
                                    rows={3}
                                    placeholder="Write a reply..."
                                    value={replyMessage}
                                    onChange={(event) => setReplyMessage(event.target.value)}
                                  />
                                  {replyError && (
                                    <div className="support-page__msg support-page__msg--error">{replyError}</div>
                                  )}
                                  <button
                                    type="submit"
                                    className="support-page__submit-btn"
                                    disabled={!replyMessage.trim() || replySubmitting}
                                  >
                                    {replySubmitting ? 'Sending...' : 'Send reply'}
                                  </button>
                                </form>
                              )}
                            </>
                          ) : (
                            <div className="support-page__msg support-page__msg--error">{replyError || 'Failed to load ticket.'}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <Footer />
      </main>
    </div>
  );
}

export default SupportPage;
